import { useProfileStatus } from '@/hooks/useProfileStatus';
import { useStudyYearPeriods } from '@/pages/Overview/hooks/useStudyYearPeriods';
import { useStudyYearStart } from '@/pages/Overview/hooks/useStudyYearStart';
import { StudyYearPeriodType } from '@/types/overview';
import { ProfileStatus } from '@/types/user';
import { clampDate, startOfDay, startOfToday } from '@/utils/date';

const LAST_LESSON_MONTH_INDEX = 4;
const LAST_LESSON_DAY = 31;
const STUDY_YEAR_START_MONTH_INDEX = 8;
const STUDY_YEAR_END_MONTH_INDEX = 7;
const STUDY_YEAR_END_DAY = 31;

interface DiaryDateRange {
  from: Date;
  to: Date;
  defaultSelectedDate: Date;
  isCurrentStudyYear: boolean;
}

interface PreferredDateParams {
  from: Date;
  to: Date;
  profileStatus: ProfileStatus | null;
}

const getPreferredDate = ({
  from,
  to,
  profileStatus,
}: PreferredDateParams): Date => {
  if (profileStatus === ProfileStatus.NextStudyYear) {
    return from;
  }

  if (profileStatus === ProfileStatus.PastStudyYear) {
    return new Date(to.getFullYear(), LAST_LESSON_MONTH_INDEX, LAST_LESSON_DAY);
  }

  return startOfToday();
};

export const useDiaryDateRange = (): DiaryDateRange => {
  const studyYearStart = useStudyYearStart();
  const profileStatus = useProfileStatus();
  const { periods } = useStudyYearPeriods();

  const yearPeriod = periods.find(
    (period) => period.type === StudyYearPeriodType.Year,
  );
  const hasBrowsableYear = !!yearPeriod && yearPeriod.from < yearPeriod.to;

  const from = hasBrowsableYear
    ? startOfDay(yearPeriod.from)
    : new Date(studyYearStart, STUDY_YEAR_START_MONTH_INDEX, 1);
  const to = hasBrowsableYear
    ? startOfDay(yearPeriod.to)
    : new Date(
        studyYearStart + 1,
        STUDY_YEAR_END_MONTH_INDEX,
        STUDY_YEAR_END_DAY,
      );

  return {
    from,
    to,
    defaultSelectedDate: clampDate({
      date: getPreferredDate({ from, to, profileStatus }),
      from,
      to,
    }),
    isCurrentStudyYear:
      profileStatus !== ProfileStatus.NextStudyYear &&
      profileStatus !== ProfileStatus.PastStudyYear,
  };
};
